import { useLeagueSportsbook } from '@hooks/useLeagueSportsbook';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { choosableBooksForLeague, getBookDisplayName } from '@parlay-picker/shared/sportsbook';
import { type ReactElement, useRef } from 'react';

interface SportsbookSelectorProps {
  leagueKey: LeagueKey;
}

export function SportsbookSelector({ leagueKey }: SportsbookSelectorProps): ReactElement {
  const { sportsbook: currentSportsbook, setSportsbook } = useLeagueSportsbook(leagueKey);
  const sportsbookSelectorRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: sportsbookSelectorOverflows } = useScrollOverflow(sportsbookSelectorRef);

  return (
    <div className="mb-4">
      <p className="text-xs text-theme-text-tertiary mb-1.5">Sportsbook</p>
      <div className="relative">
        {sportsbookSelectorOverflows && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-surface to-transparent z-10 pointer-events-none" />
        )}
        <div
          ref={sportsbookSelectorRef}
          className="flex gap-1.5 pb-0.5 overflow-x-auto scrollbar-none"
        >
          {choosableBooksForLeague(leagueKey).map((book) => (
            <button
              key={book}
              type="button"
              onClick={() => {
                if (book !== currentSportsbook) {
                  setSportsbook(book);
                }
              }}
              className={`text-xs px-2.5 py-1.5 rounded-full font-medium transition-all whitespace-nowrap shrink-0 ${
                currentSportsbook === book
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
              }`}
            >
              {getBookDisplayName(book)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
